import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

export async function uploadProfilePicture(userId: string, file: File) {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload an image file');
  }

  // Validate file size (max 5MB)
  const MAX_SIZE = 5 * 1024 * 1024;
  if (file.size > MAX_SIZE) {
    throw new Error('Image must be less than 5MB');
  }

  try {
    // Create the user's folder if it doesn't exist
    const folderPath = `${userId}`;
    
    // Delete existing avatar if it exists
    const { data: existingFiles } = await supabase.storage 
      .from('avatars')
      .list(folderPath);

    if (existingFiles?.length) {
      await Promise.all(
        existingFiles.map(file => 
          supabase.storage
            .from('avatars')
            .remove([`${folderPath}/${file.name}`])
        )
      );
    }

    // Convert image to PNG using canvas
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

    // Set canvas size to maintain aspect ratio
    const MAX_DIMENSION = 512; // Max width/height for the image
    let width = img.width;
    let height = img.height;
    
    if (width > height && width > MAX_DIMENSION) {
      height = (height * MAX_DIMENSION) / width;
      width = MAX_DIMENSION;
    } else if (height > MAX_DIMENSION) {
      width = (width * MAX_DIMENSION) / height;
      height = MAX_DIMENSION;
    }

    canvas.width = width;
    canvas.height = height;
    ctx?.drawImage(img, 0, 0, width, height);

    // Convert canvas to PNG blob
    const pngBlob = await new Promise<Blob>((resolve) => {
      canvas.toBlob(blob => resolve(blob!), 'image/png', 1);
    });

    // Create PNG file with timestamp to ensure uniqueness
    const timestamp = Date.now();
    const filePath = `${folderPath}/avatar_${timestamp}.png`;

    // Upload the PNG file
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, pngBlob, {
        contentType: 'image/png',
        cacheControl: 'no-cache',
        upsert: false
      });

    if (uploadError) throw uploadError;

    // Get the public URL
    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    // Update user profile with new avatar URL
    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({ 
        avatar_url: publicUrl,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (updateError) throw updateError;

    // Clean up
    URL.revokeObjectURL(img.src);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    toast.error('Failed to upload profile picture');
    throw error;
  }
}